import * as _ from 'underscore';
import { ITypeaheadSource } from '../directives/tbTypeahead';
import { FeatureFlags } from '../FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { Filter } from '../helpers/Filter';
import { Order } from '../helpers/Order';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { ScheduleContentType } from '../models/ContentTypes';
import { ISliceable } from '../models/ISliceable';
import { Module } from '../Module';
import { DataConnectionTypes } from '../resources/DataConnectionTypes';
import { Favorites } from '../resources/Favorites';
import { Groups } from '../resources/Groups';
import { PrincipalNames } from '../resources/PrincipalNames';
import { ProjectNames } from '../resources/ProjectNames';
import { ResourceQuery } from '../resources/ResourceQuery';
import { Schedules } from '../resources/Schedules';
import { SiteNameSansIds } from '../resources/SiteNameSansIds';
import { Tags } from '../resources/Tags';
import { UserNames } from '../resources/UserNames';
import { Users } from '../resources/Users';
import { DataConnectionTypeKeys, IDataConnectionType } from '../ServerApi/types/IDataConnection';
import { IFavorite } from '../ServerApi/types/IFavorite';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { GroupKeys, IGroup } from '../ServerApi/types/IGroup';
import { IProjectName } from '../ServerApi/types/IProject';
import { ISchedule } from '../ServerApi/types/ISchedule';
import { ISiteNameSansId } from '../ServerApi/types/ISiteNameSansId';
import { ITag } from '../ServerApi/types/ITag';
import { IUserName, UserKeys } from '../ServerApi/types/IUser';
import { ITypedItem, NamedItemKeys } from '../ServerApi/types/resourceParts';
import { ServerService } from './ServerService';
import { SliceableFactory } from './SliceableFactory';

export class SearchSuggestionsService {

  public static $inject = [
    'SliceableFactory',
    'ProjectNames',
    'UserNames',
    'PrincipalNames',
    'Groups',
    'Tags',
    'DataConnectionTypes',
    'SiteNamesAcrossAllPods',
    'Favorites',
    'Schedules',
    'Users',
    'ServerService',
    'scheduleFrequencyDescriptionFilter'
  ];

  constructor(
    private SliceableFactory: SliceableFactory,
    private ProjectNames: ProjectNames,
    private UserNames: UserNames,
    private PrincipalNames: PrincipalNames,
    private Groups: Groups,
    private Tags: Tags,
    private DataConnectionTypes: DataConnectionTypes,
    private SiteNamesAcrossAllPods: SiteNameSansIds,
    private Favorites: Favorites,
    private Schedules: Schedules,
    private Users: Users,
    private ServerService: ServerService,
    private ScheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter
  ) { }

  public project(id: string): ng.IPromise<IFieldValue> {
    return this.ProjectNames.getById(id).then((projectName: IProjectName) => {
      return {
        value: projectName ? projectName.id : id,
        label: projectName ? projectName.name : '',
        count: 0
      };
    });
  }

  public sites(matchText: string): ISliceable<ISiteNameSansId> {
    return this.SiteNamesAcrossAllPods.all().withFilters([Filter.search(matchText)]);
  }

  public favorites(matchText: string): ISliceable<IFavorite> {
    return this.Favorites.all().withFilters([Filter.search(matchText)]);
  }

  public principalNames(matchText: string): ResourceQuery<ITypedItem> {
    if (FeatureFlags.getGroupAndUserNamesEnabled) {
      return this.PrincipalNames.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(NamedItemKeys.name)]);
    }
  }

  public users(matchText: string): ResourceQuery<IUserName> {
    return this.UserNames.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(UserKeys.displayName)]);
  }

  public usersWithoutGuest(matchText: string): ResourceQuery<IUserName> {
    return this.users(matchText).addFilter(this.Users.WithoutGuestFilter);
  }

  public adminUsers(matchText: string): ResourceQuery<IUserName> {
    return this.users(matchText).addFilter(this.Users.AdminsOnlyFilter);
  }

  public usersCanOwnProject(matchText: string): ResourceQuery<IUserName> {
    return this.users(matchText).addFilter(this.Users.CanOwnProjectFilter);
  }

  public user(id: string): ng.IPromise<IFieldValue> {
    return this.UserNames.getById(id).then((userName: IUserName) => {
      return {
        value: userName ? userName.id : id,
        label: userName ? userName.displayName : '',
        count: 0
      };
    });
  }

  public groups(matchText: string): ISliceable<IGroup> {
    return this.Groups.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(GroupKeys.name)]);
  }

  public group(id: string): ng.IPromise<IGroup> {
    return this.Groups.getById(id);
  }

  public tags(matchText: string): ISliceable<ITag> {
    return this.Tags.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(NamedItemKeys.name)]);
  }

  public dataConnectionType(dataConnectionType: string): ng.IPromise<IFieldValue> {
    return this.DataConnectionTypes.getByDataConnectionType(dataConnectionType).then((connectionType: IDataConnectionType) => {
      return {
        value: connectionType ? connectionType.connectionType : dataConnectionType,
        label: connectionType ? connectionType.connectionTypeDisplayName : '',
        count: 0
      };
    });
  }

  public dataConnectionTypes(matchText: string): ISliceable<IDataConnectionType> {
    return this.DataConnectionTypes.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(DataConnectionTypeKeys.connectionTypeDisplayName)]);
  }

  public schedulesSource(scheduledActionType?: string): ITypeaheadSource {
    const searchFilterFn = (schedule: ISchedule, search: string) => {
      if(!search) return true;

      const scheduleName = this.ServerService.displayScheduleDescriptionAsName() ? this.ScheduleFrequencyDescriptionFilter(schedule) : schedule.name;
      return _.all(search.toLowerCase().split(' '), (searchPart) => (scheduleName.toLowerCase().indexOf(searchPart) >= 0));
    };

    let scheduleResourceQuery = this.Schedules.all().withOrder(ScheduleContentType.defaultOrder);
    if(scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(Schedules.filterByActionType(scheduledActionType));
    }

    const scheduleListPromise = SliceableHelper.fetchAll(scheduleResourceQuery).then((schedulesList) => schedulesList.items);

    return this.SliceableFactory.searchArray(searchFilterFn, scheduleListPromise);
  }

}

Module.instance().service('SearchSuggestionsService', SearchSuggestionsService);
